import { createContext, useContext, useState, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import krMessages from '../locales/kr/messages.json';
import enMessages from '../locales/en/messages.json';

const messages = {
  kr: krMessages,
  en: enMessages,
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const getLanguageFromURL = () => {
    const params = new URLSearchParams(location.search);
    const lang = params.get('lang');
    
    if (lang && (lang === 'kr' || lang === 'en')) {
      return lang;
    }
    
    return 'kr';
  };

  const [language, setLanguage] = useState(getLanguageFromURL());

  useEffect(() => {
    const newLang = getLanguageFromURL();
    if (newLang !== language) {
      setLanguage(newLang);
    }
  }, [location.search]);

  const changeLanguage = (newLang) => {
    if (newLang !== language) {
      const params = new URLSearchParams(location.search);
      params.set('lang', newLang);
      navigate({ search: params.toString() });
      setLanguage(newLang);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentLang = params.get('lang');
    
    if (!currentLang || (currentLang !== 'kr' && currentLang !== 'en')) {
      params.set('lang', 'kr');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location.pathname]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      <IntlProvider 
        messages={messages[language]} 
        locale={language}
        defaultLocale="kr"
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};